import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './DataEntryTab.css';
import { format, isValid, parseISO } from 'date-fns';
import ConfirmModal from './ConfirmModal';

// Custom hook for credit calculations
const useCreditCalculator = (customerCredits, employeeCredits) => {
  const calculateTotalCredit = useCallback((date, group, timeGroup) => {
    if (!date || !group || !timeGroup) return 0;
    
    try {
      const entryDate = format(new Date(date), 'yyyy-MM-dd');
      
      const associatedCustomerCredits = (customerCredits || []).filter(credit => {
        if (!credit || !credit.date) return false;
        const creditDate = format(new Date(credit.date), 'yyyy-MM-dd');
        return creditDate === entryDate &&
               credit.group === group &&
               credit.time_group === timeGroup;
      });

      const associatedEmployeeCredits = (employeeCredits || []).filter(credit => {
        if (!credit || !credit.date) return false;
        const creditDate = format(new Date(credit.date), 'yyyy-MM-dd');
        return creditDate === entryDate &&
               credit.group === group &&
               credit.time_group === timeGroup;
      });

      const customerTotal = associatedCustomerCredits.reduce((sum, credit) => 
        sum + (parseFloat(credit.amount) || 0), 0);
      
      const employeeTotal = associatedEmployeeCredits.reduce((sum, credit) => 
        sum + (parseFloat(credit.amount) || 0), 0);
      
      return customerTotal + employeeTotal;
    } catch (error) {
      console.error('Error calculating total credit:', error);
      return 0;
    }
  }, [customerCredits, employeeCredits]);

  return { calculateTotalCredit };
};

// Custom hook for dropdown outside click handling
const useClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

// Sub-components for better organization
const SearchableDropdown = ({ 
  value, 
  options, 
  searchQuery, 
  onSearchChange, 
  onSelect, 
  placeholder,
  onAddNew,
  showDropdown,
  onToggleDropdown,
  className = ''
}) => {
  const dropdownRef = useRef(null);
  
  useClickOutside(dropdownRef, () => {
    if (showDropdown) onToggleDropdown(false);
  });

  const filteredOptions = useMemo(() => 
    options.filter(option =>
      option.name.toLowerCase().includes(searchQuery.toLowerCase())
    ), [options, searchQuery]
  );

  return (
    <div className={`searchable-dropdown ${className} ${showDropdown ? 'active' : ''}`} ref={dropdownRef}>
      <div 
        className="dropdown-trigger"
        onClick={() => onToggleDropdown(!showDropdown)}
      >
        <input
          type="text"
          value={value || ''}
          onChange={() => {}}
          placeholder={placeholder}
          required
          readOnly
        />
        <span className="dropdown-arrow">▼</span>
      </div>
      {showDropdown && (
        <div className="dropdown-menu">
          <div className="dropdown-search">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={`Search ${placeholder}...`}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="dropdown-options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <div
                  key={option._id || option.id}
                  className={`dropdown-option ${value === option.name ? 'selected' : ''}`}
                  onClick={() => {
                    onSelect(option.name);
                    onSearchChange('');
                    onToggleDropdown(false);
                  }}
                >
                  {option.name}
                </div>
              ))
            ) : (
              <div className="dropdown-option no-results">
                No results found
              </div>
            )}
          </div>
          {onAddNew && (
            <div className="dropdown-footer">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddNew();
                  onToggleDropdown(false);
                }}
                className="btn-add-new"
              >
                + Add New
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ResultsPanel = ({ results, formData, settings }) => {
  if (results.total_calculated_formula === null) return null;

  const totalCalculated = (parseFloat(formData.billet || 0) + parseFloat(formData.money || 0)).toFixed(2);

  return (
    <div className="results-panel">
      <h3>Calculation Results</h3>
      <div className="results-grid">
        <div className="result-item">
          <span className="result-label">Total Calculated (Billet + Money):</span>
          <span className="result-value">
            {totalCalculated}{settings.currency}
          </span>
        </div>
        <div className="result-item">
          <span className="result-label">Total Calculated Formula:</span>
          <span className="result-value">
            {results.total_calculated_formula.toFixed(2)}{settings.currency}
          </span>
        </div>
        <div className="result-item">
          <span className="result-label">Difference (Calculated vs Journal):</span>
          <span className={`result-value ${results.difference >= 0 ? 'positive' : 'negative'}`}>
            {results.difference.toFixed(2)}{settings.currency}
          </span>
        </div>
        <div className="result-item">
          <span className="result-label">Daily Revenue:</span>
          <span className="result-value">
            {results.daily_revenue.toFixed(2)}{settings.currency}
          </span>
        </div>
      </div>
    </div>
  );
};

const InputWithCurrency = ({
  value, 
  onChange, 
  onBlur, 
  name, 
  label, 
  required = false, 
  readOnly = false,
  placeholder = "0.00",
  step = "0.01",
  min = "0",
  settings,
  className = '',
  helperText = ''
}) => (
  <div className="form-group">
    {label && (
      <label>
        {label}
        {required && <span style={{color: '#f44336'}}>*</span>}
      </label>
    )}
    <div className="input-with-currency">
      <input
        type="number"
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        step={step}
        min={min}
        placeholder={placeholder}
        required={required}
        readOnly={readOnly}
        className={readOnly ? 'readonly-input' : ''}
      />
      <span className="currency">{settings.currency}</span>
    </div>
    {helperText && (
      <small className="input-helper-text">{helperText}</small>
    )}
  </div>
);

const DataEntryTab = ({ 
  onAddEntry,
  onUpdateEntry,
  onAddCustomerCredit, 
  onAddEmployeeCredit, 
  customerCredits, 
  employeeCredits, 
  customers, 
  employees,
  entries = [],
  onRefreshCustomers, 
  settings, 
  toast 
}) => {
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    group: settings.groups[0] || '',
    time_group: settings.timeGroups?.[0] || '7am to 2pm',
    billet: '',
    money: '',
    font_caisse: '',
    total_credit: '',
    total_achat: '',
    total_journal: '',
    notes: ''
  });

  const [customerCredit, setCustomerCredit] = useState({
    customer_name: '',
    amount: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    group: settings.groups[0] || '',
    time_group: settings.timeGroups?.[0] || '7am to 2pm',
    notes: ''
  });

  const [employeeCredit, setEmployeeCredit] = useState({
    employee_name: '',
    amount: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    group: settings.groups[0] || '',
    time_group: settings.timeGroups?.[0] || '7am to 2pm',
    notes: ''
  });

  const [results, setResults] = useState({
    total_calculated_formula: null,
    difference: null,
    daily_revenue: null
  });

  const [newCustomerName, setNewCustomerName] = useState('');
  const [showNewCustomerInput, setShowNewCustomerInput] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [duplicateEntry, setDuplicateEntry] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [pendingEntry, setPendingEntry] = useState(null);

  const prevEntryKeyRef = useRef('');
  const { calculateTotalCredit } = useCreditCalculator(customerCredits, employeeCredits);

  // Check for duplicate entry
  const checkDuplicateEntry = useCallback((date, group, timeGroup) => {
    if (!entries || entries.length === 0) return null;
    
    const entryDate = format(new Date(date), 'yyyy-MM-dd');
    
    return entries.find(entry => {
      if (!entry || !entry.date) return false;
      try {
        const existingDate = format(new Date(entry.date), 'yyyy-MM-dd');
        return existingDate === entryDate &&
               entry.group === group &&
               entry.time_group === timeGroup;
      } catch (e) {
        return false;
      }
    });
  }, [entries]);

  // Check if current form has a duplicate (for warning display)
  const existingEntry = useMemo(() => {
    if (!formData.date || !formData.group || !formData.time_group) return null;
    return checkDuplicateEntry(formData.date, formData.group, formData.time_group);
  }, [formData.date, formData.group, formData.time_group, checkDuplicateEntry]);

  // Sync customer credit form with entry form
  useEffect(() => {
    setCustomerCredit(prev => ({
      ...prev,
      date: formData.date,
      group: formData.group,
      time_group: formData.time_group
    }));
    
    setEmployeeCredit(prev => ({
      ...prev,
      date: formData.date,
      group: formData.group,
      time_group: formData.time_group
    }));
  }, [formData.date, formData.group, formData.time_group]);

  // Auto-calculate total credit
  useEffect(() => {
    const currentEntryKey = `${formData.date}-${formData.group}-${formData.time_group}`;
    
    if (prevEntryKeyRef.current !== currentEntryKey && prevEntryKeyRef.current !== '') {
      setFormData(prev => ({ 
        ...prev, 
        total_credit: '0' 
      }));
    }
    
    prevEntryKeyRef.current = currentEntryKey;
    
    const totalCredit = calculateTotalCredit(formData.date, formData.group, formData.time_group);
    
    setFormData(prev => {
      const currentCredit = parseFloat(prev.total_credit) || 0;
      if (Math.abs(currentCredit - totalCredit) > 0.01) {
        return { 
          ...prev, 
          total_credit: totalCredit.toFixed(2) 
        };
      }
      return prev;
    });
  }, [formData.date, formData.group, formData.time_group, calculateTotalCredit]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    
    if (e.target.type === 'number') {
      const numValue = parseFloat(value);
      if (isNaN(numValue) || value === '' || value === null || value === undefined) {
        setFormData(prev => ({
          ...prev,
          [name]: ''
        }));
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleNumberBlur = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return '';
    }
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return '';
    }
    
    const rounded = Math.round(numValue * 100) / 100;
    return rounded >= 0 ? rounded.toString() : '';
  }, []);

  const calculate = useCallback(() => {
    const billet = parseFloat(formData.billet) || 0;
    const money = parseFloat(formData.money) || 0;
    const total_calculated = billet + money;
    const font_caisse = parseFloat(formData.font_caisse) || 0;
    const total_credit = parseFloat(formData.total_credit) || 0;
    const total_achat = parseFloat(formData.total_achat) || 0;
    const total_journal = parseFloat(formData.total_journal) || 0;

    const total_calculated_formula = total_calculated + total_credit + total_achat - font_caisse;
    const difference = total_calculated_formula - total_journal;
    const daily_revenue = total_journal - total_achat;

    setResults({
      total_calculated_formula,
      difference,
      daily_revenue
    });
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate required fields
      if (!formData.date) {
        throw new Error('Please select a date');
      }
      if (!formData.group) {
        throw new Error('Please select a group');
      }
      if (!formData.time_group) {
        throw new Error('Please select a time group');
      }

      const billet = parseFloat(formData.billet);
      if (isNaN(billet) || billet < 0) {
        throw new Error('Billet cannot be empty. Please enter a valid amount.');
      }

      const money = parseFloat(formData.money);
      if (isNaN(money) || money < 0) {
        throw new Error('Money cannot be empty. Please enter a valid amount.');
      }

      const font_caisse = parseFloat(formData.font_caisse);
      if (isNaN(font_caisse) || font_caisse < 0) {
        throw new Error('Font de Caisse cannot be empty. Please enter a valid amount.');
      }

      const total_journal = parseFloat(formData.total_journal);
      if (isNaN(total_journal) || total_journal <= 0) {
        throw new Error('Journal de Ventes cannot be 0 or empty. Please enter a valid amount.');
      }

      const total_calculated = billet + money;
      const calculatedTotalCredit = calculateTotalCredit(formData.date, formData.group, formData.time_group);

      const entry = {
        ...formData,
        billet,
        money,
        total_calculated,
        font_caisse,
        total_credit: calculatedTotalCredit,
        total_achat: parseFloat(formData.total_achat) || 0,
        total_journal
      };

      // Check for duplicate entry
      const existingEntry = checkDuplicateEntry(formData.date, formData.group, formData.time_group);
      
      if (existingEntry) {
        // Store the entry and show confirmation modal
        setPendingEntry(entry);
        setDuplicateEntry(existingEntry);
        setShowDuplicateModal(true);
        setIsLoading(false);
        return;
      }

      await onAddEntry(entry);
      
      // Reset form
      const newDate = format(new Date(), 'yyyy-MM-dd');
      const newGroup = settings.groups[0] || '';
      const newTimeGroup = settings.timeGroups?.[0] || '7am to 2pm';
      
      prevEntryKeyRef.current = '';
      
      setFormData({
        date: newDate,
        group: newGroup,
        time_group: newTimeGroup,
        billet: '',
        money: '',
        font_caisse: '',
        total_credit: '0',
        total_achat: '',
        total_journal: '',
        notes: ''
      });
      
      setCustomerCredit({
        customer_name: '',
        amount: '',
        date: newDate,
        group: newGroup,
        time_group: newTimeGroup,
        notes: ''
      });
      
      setResults({
        total_calculated_formula: null,
        difference: null,
        daily_revenue: null
      });

      toast?.success('Entry saved successfully!');
    } catch (error) {
      toast?.error(error.message || 'Failed to save entry');
    } finally {
      setIsLoading(false);
    }
  }, [formData, calculateTotalCredit, onAddEntry, settings, toast, checkDuplicateEntry]);

  // Handle duplicate entry update
  const handleUpdateDuplicate = useCallback(async () => {
    if (!pendingEntry || !duplicateEntry) return;
    
    setIsLoading(true);
    try {
      const entryId = duplicateEntry._id || duplicateEntry.id;
      if (onUpdateEntry && entryId) {
        await onUpdateEntry(entryId, pendingEntry);
        
        // Reset form
        const newDate = format(new Date(), 'yyyy-MM-dd');
        const newGroup = settings.groups[0] || '';
        const newTimeGroup = settings.timeGroups?.[0] || '7am to 2pm';
        
        prevEntryKeyRef.current = '';
        
        setFormData({
          date: newDate,
          group: newGroup,
          time_group: newTimeGroup,
          billet: '',
          money: '',
          font_caisse: '',
          total_credit: '0',
          total_achat: '',
          total_journal: '',
          notes: ''
        });
        
        setCustomerCredit({
          customer_name: '',
          amount: '',
          date: newDate,
          group: newGroup,
          time_group: newTimeGroup,
          notes: ''
        });
        
        setResults({
          total_calculated_formula: null,
          difference: null,
          daily_revenue: null
        });

        toast?.success('Entry updated successfully!');
      }
    } catch (error) {
      toast?.error(error.message || 'Failed to update entry');
    } finally {
      setIsLoading(false);
      setShowDuplicateModal(false);
      setDuplicateEntry(null);
      setPendingEntry(null);
    }
  }, [pendingEntry, duplicateEntry, onUpdateEntry, settings, toast]);

  // Handle duplicate entry cancel
  const handleCancelDuplicate = useCallback(() => {
    setShowDuplicateModal(false);
    setDuplicateEntry(null);
    setPendingEntry(null);
  }, []);

  const handleClear = useCallback(() => {
    const newDate = format(new Date(), 'yyyy-MM-dd');
    const newGroup = settings.groups[0] || '';
    const newTimeGroup = settings.timeGroups?.[0] || '7am to 2pm';
    
    setFormData({
      date: newDate,
      group: newGroup,
      time_group: newTimeGroup,
      billet: '',
      money: '',
      font_caisse: '',
      total_credit: '',
      total_achat: '',
      total_journal: '',
      notes: ''
    });
    
    setCustomerCredit({
      customer_name: '',
      amount: '',
      date: newDate,
      group: newGroup,
      time_group: newTimeGroup,
      notes: ''
    });
    
    setResults({
      total_calculated_formula: null,
      difference: null,
      daily_revenue: null
    });
  }, [settings]);

  const handleSaveCredit = useCallback(async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!customerCredit.customer_name || !customerCredit.amount) {
        throw new Error('Please fill in customer name and amount');
      }

      const amount = parseFloat(customerCredit.amount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Please enter a valid amount');
      }

      await onAddCustomerCredit({
        ...customerCredit,
        amount,
        date: new Date(customerCredit.date),
        group: formData.group,
        time_group: formData.time_group
      });
      
      setCustomerCredit(prev => ({
        ...prev,
        customer_name: '',
        amount: '',
        notes: ''
      }));
      
      setShowNewCustomerInput(false);
      setNewCustomerName('');
      setCustomerSearchQuery('');
      
      toast?.success('Customer credit saved successfully! Total Credit will be updated automatically.');
    } catch (error) {
      toast?.error(error.message || 'Failed to save credit');
    } finally {
      setIsLoading(false);
    }
  }, [customerCredit, onAddCustomerCredit, formData.group, formData.time_group, toast]);

  const handleSaveEmployeeCredit = useCallback(async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (!employeeCredit.employee_name || !employeeCredit.amount) {
        throw new Error('Please fill in employee name and amount');
      }

      const amount = parseFloat(employeeCredit.amount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Please enter a valid amount');
      }

      await onAddEmployeeCredit({
        ...employeeCredit,
        amount,
        date: new Date(employeeCredit.date),
        group: formData.group,
        time_group: formData.time_group
      });
      
      setEmployeeCredit(prev => ({
        ...prev,
        employee_name: '',
        amount: '',
        notes: ''
      }));
      
      setEmployeeSearchQuery('');
      
      toast?.success('Employee credit saved successfully! Total Credit will be updated automatically.');
    } catch (error) {
      toast?.error(error.message || 'Failed to save employee credit');
    } finally {
      setIsLoading(false);
    }
  }, [employeeCredit, onAddEmployeeCredit, formData.group, formData.time_group, toast]);

  const handleAddNewCustomer = useCallback(async () => {
    if (!newCustomerName.trim()) {
      toast?.error('Please enter a customer name');
      return;
    }

    setIsLoading(true);
    try {
      const { customersAPI } = await import('../services/api');
      const newCustomer = await customersAPI.create({ name: newCustomerName.trim() });
      
      setCustomerCredit(prev => ({ ...prev, customer_name: newCustomer.name }));
      setNewCustomerName('');
      setShowNewCustomerInput(false);
      setCustomerSearchQuery('');
      
      if (onRefreshCustomers) {
        await onRefreshCustomers();
      }
      
      toast?.success('Customer added successfully!');
    } catch (error) {
      toast?.error('Failed to add customer: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  }, [newCustomerName, toast, onRefreshCustomers]);

  const memoizedCustomers = useMemo(() => customers || [], [customers]);
  const memoizedEmployees = useMemo(() => employees || [], [employees]);
  const timeGroups = useMemo(() => 
    settings.timeGroups || ['7am to 2pm', '2pm to 10pm'], 
    [settings.timeGroups]
  );

  return (
    <div className="data-entry-tab">
      <h2>Daily Revenue Entry</h2>
      {isLoading && <div className="loading-overlay">Loading...</div>}
      
      <form onSubmit={handleSubmit} className="entry-form">
        <div className="form-section">
          <h3 className="section-title">Basic Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Date: <span style={{color: '#f44336'}}>*</span></label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Group: <span style={{color: '#f44336'}}>*</span></label>
              <select
                name="group"
                value={formData.group}
                onChange={handleChange}
                required
              >
                {settings.groups.map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Time Group: <span style={{color: '#f44336'}}>*</span></label>
              <select
                name="time_group"
                value={formData.time_group}
                onChange={handleChange}
                required
              >
                {timeGroups.map(timeGroup => (
                  <option key={timeGroup} value={timeGroup}>{timeGroup}</option>
                ))}
              </select>
            </div>
          </div>
          {existingEntry && (
            <div className="duplicate-warning" style={{
              padding: '12px 16px',
              background: '#fff3cd',
              border: '2px solid #ffc107',
              borderRadius: '8px',
              marginTop: '16px',
              color: '#856404',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              ⚠️ An entry already exists for this date, group, and time group. Saving will update the existing entry.
            </div>
          )}
        </div>

        <div className="form-section">
          <h3 className="section-title">Cash Collection</h3>
          <div className="form-row">
            <InputWithCurrency
              value={formData.billet}
              onChange={handleChange}
              onBlur={(e) => {
                const cleaned = handleNumberBlur('billet', e.target.value);
                setFormData(prev => ({ ...prev, billet: cleaned }));
              }}
              name="billet"
              label="Billet:"
              required
              placeholder="0.00"
              settings={settings}
            />

            <InputWithCurrency
              value={formData.money}
              onChange={handleChange}
              onBlur={(e) => {
                const cleaned = handleNumberBlur('money', e.target.value);
                setFormData(prev => ({ ...prev, money: cleaned }));
              }}
              name="money"
              label="Money:"
              required
              placeholder="0.00"
              settings={settings}
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Adjustments & Credits</h3>
          <div className="form-row">
            <InputWithCurrency
              value={formData.font_caisse}
              onChange={handleChange}
              onBlur={(e) => {
                const cleaned = handleNumberBlur('font_caisse', e.target.value);
                setFormData(prev => ({ ...prev, font_caisse: cleaned }));
              }}
              name="font_caisse"
              label="Font de Caisse J-1:"
              required
              placeholder="0.00"
              settings={settings}
              helperText="Cash from previous day (J-1)"
            />

            <InputWithCurrency
              value={formData.total_credit}
              onChange={handleChange}
              name="total_credit"
              label="Total Credit:"
              placeholder="0.00"
              settings={settings}
              readOnly
              helperText="Auto-calculated from customer & employee credits"
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Sales & Purchases</h3>
          <div className="form-row">
            <InputWithCurrency
              value={formData.total_achat}
              onChange={handleChange}
              onBlur={(e) => {
                const cleaned = handleNumberBlur('total_achat', e.target.value);
                setFormData(prev => ({ ...prev, total_achat: cleaned }));
              }}
              name="total_achat"
              label="Total Achat:"
              placeholder="0.00"
              settings={settings}
              helperText="Total purchases/expenses for the day"
            />

            <InputWithCurrency
              value={formData.total_journal}
              onChange={handleChange}
              onBlur={(e) => {
                const cleaned = handleNumberBlur('total_journal', e.target.value);
                if (cleaned && parseFloat(cleaned) > 0) {
                  setFormData(prev => ({ ...prev, total_journal: cleaned }));
                } else {
                  setFormData(prev => ({ ...prev, total_journal: '' }));
                }
              }}
              name="total_journal"
              label="Total Journal de Ventes:"
              required
              placeholder="0.00"
              min="0.01"
              settings={settings}
              helperText="Total sales from journal (required, must be greater than 0)"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Notes:</label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="3"
            placeholder="Additional notes..."
          />
        </div>

        <div className="form-actions">
          <button type="button" onClick={calculate} className="btn btn-secondary" disabled={isLoading}>
            Calculate
          </button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save Entry'}
          </button>
          <button type="button" onClick={handleClear} className="btn btn-danger" disabled={isLoading}>
            Clear
          </button>
        </div>
      </form>

      <ResultsPanel results={results} formData={formData} settings={settings} />

      <div className="credits-section">
        <div className="customer-credit-section">
          <h3>Customer Credit</h3>
          <p className="credit-info">
            Credits will be automatically associated with the current entry (Date: {formData.date}, Group: {formData.group}, Time: {formData.time_group})
          </p>
          <form onSubmit={handleSaveCredit} className="credit-form">
            <div className="form-row">
              <div className="form-group">
                <label>Customer Name:</label>
                {!showNewCustomerInput ? (
                  <SearchableDropdown
                    value={customerCredit.customer_name}
                    options={memoizedCustomers}
                    searchQuery={customerSearchQuery}
                    onSearchChange={setCustomerSearchQuery}
                    onSelect={(name) => setCustomerCredit(prev => ({ ...prev, customer_name: name }))}
                    placeholder="Search or select customer..."
                    onAddNew={() => setShowNewCustomerInput(true)}
                    showDropdown={showCustomerDropdown}
                    onToggleDropdown={setShowCustomerDropdown}
                    className="customer-dropdown"
                  />
                ) : (
                  <>
                    <input
                      type="text"
                      value={newCustomerName}
                      onChange={(e) => setNewCustomerName(e.target.value)}
                      placeholder="Enter new customer name"
                      required
                    />
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <button
                        type="button"
                        onClick={handleAddNewCustomer}
                        className="btn btn-primary"
                        style={{ fontSize: '14px', padding: '8px 16px' }}
                        disabled={isLoading}
                      >
                        Save Customer
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewCustomerInput(false);
                          setNewCustomerName('');
                        }}
                        className="btn btn-secondary"
                        style={{ fontSize: '14px', padding: '8px 16px' }}
                        disabled={isLoading}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>

              <InputWithCurrency
                value={customerCredit.amount}
                onChange={(e) => setCustomerCredit(prev => ({ ...prev, amount: e.target.value }))}
                onBlur={(e) => {
                  const cleaned = handleNumberBlur('amount', e.target.value);
                  setCustomerCredit(prev => ({ ...prev, amount: cleaned }));
                }}
                name="amount"
                label="Amount:"
                required
                placeholder="0.00"
                settings={settings}
              />
            </div>

            <div className="form-group">
              <label>Notes:</label>
              <textarea
                name="notes"
                value={customerCredit.notes}
                onChange={(e) => setCustomerCredit(prev => ({ ...prev, notes: e.target.value }))}
                rows="2"
                placeholder="Additional notes about the credit..."
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save Customer Credit'}
              </button>
            </div>
          </form>
        </div>

        <div className="employee-credit-section">
          <h3>Employee Credit</h3>
          <p className="credit-info">
            Employee credits will be automatically associated with the current entry (Date: {formData.date}, Group: {formData.group}, Time: {formData.time_group})
          </p>
          <form onSubmit={handleSaveEmployeeCredit} className="credit-form">
            <div className="form-row">
              <div className="form-group">
                <label>Employee Name:</label>
                <SearchableDropdown
                  value={employeeCredit.employee_name}
                  options={memoizedEmployees}
                  searchQuery={employeeSearchQuery}
                  onSearchChange={setEmployeeSearchQuery}
                  onSelect={(name) => setEmployeeCredit(prev => ({ ...prev, employee_name: name }))}
                  placeholder="Search or select employee..."
                  showDropdown={showEmployeeDropdown}
                  onToggleDropdown={setShowEmployeeDropdown}
                  className="employee-dropdown"
                />
              </div>

              <InputWithCurrency
                value={employeeCredit.amount}
                onChange={(e) => setEmployeeCredit(prev => ({ ...prev, amount: e.target.value }))}
                onBlur={(e) => {
                  const cleaned = handleNumberBlur('amount', e.target.value);
                  setEmployeeCredit(prev => ({ ...prev, amount: cleaned }));
                }}
                name="amount"
                label="Amount:"
                required
                placeholder="0.00"
                settings={settings}
              />
            </div>

            <div className="form-group">
              <label>Notes:</label>
              <textarea
                name="notes"
                value={employeeCredit.notes}
                onChange={(e) => setEmployeeCredit(prev => ({ ...prev, notes: e.target.value }))}
                rows="2"
                placeholder="Additional notes about the credit..."
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save Employee Credit'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <ConfirmModal
        isOpen={showDuplicateModal}
        onClose={handleCancelDuplicate}
        onConfirm={handleUpdateDuplicate}
        title="Duplicate Entry Detected"
        message={duplicateEntry ? `An entry already exists for ${format(new Date(duplicateEntry.date), 'yyyy-MM-dd')}, Group: ${duplicateEntry.group}, Time: ${duplicateEntry.time_group}. Do you want to update the existing entry?` : 'An entry already exists for this date, group, and time group. Do you want to update it?'}
        confirmText="Update Entry"
        cancelText="Cancel"
        type="warning"
      />
    </div>
  );
};

export default DataEntryTab;